import React, { useEffect } from 'react'
import { filter, map, pipe, prop, sum } from 'lodash/fp'
import { OctagonAlertIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import { usePricingAlertsQuery } from '@/entities/price-alert'

import { Button } from '@/shared/components/ui/button'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { AlertGroupBy } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { useDrawers } from '@/shared/lib/drawer-manager'

const getAlertsProductsTotalCount = pipe([
	prop('by_level'),
	map(prop('products')),
	sum,
])

export default function OptimizationGroupPricngAlertsButton() {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const optimizationGroupId = useOptimizationGroupId()
	const { openDrawer, closeDrawer } = useDrawers()
	useEffect(
		() => () => {
			closeDrawer('PRICING_ALERTS')
		},
		[]
	)
	const { data: pricingAlerts, isLoading } = usePricingAlertsQuery(
		{
			group_by: AlertGroupBy.OptimizationGroupId,
			currency,
		},
		{
			select: (data) => ({
				...data,
				by_level: filter(
					{ optimization_group_id: optimizationGroupId },
					data.by_level
				),
				by_id: filter(
					{ optimization_group_id: optimizationGroupId },
					data.by_id
				),
			}),
		}
	)

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					onClick={() => {
						ampli.alertsOpenClicked({
							alerts_of: 'og',
						})
						openDrawer('PRICING_ALERTS', {
							type: 'byOptimizationGroups',
							optimizationGroupIds: [optimizationGroupId as string],
							drawerClassName: 'assortment-alerts-eastside',
						})
					}}
					className='relative'
					size='icon'
				>
					{isLoading ? <Spinner /> : <OctagonAlertIcon />}
					{getAlertsProductsTotalCount(pricingAlerts) > 0 && (
						<span className='absolute -top-1 -right-1 flex size-3 ease-in-out'>
							<span className='absolute inline-flex size-full animate-ping rounded-full bg-destructive/50' />
							<span className='relative inline-flex size-3 rounded-full bg-destructive' />
						</span>
					)}
				</Button>
			</TooltipTrigger>
			<TooltipContent side='top' align='end'>
				{t('app.pricing_alerts')}
			</TooltipContent>
		</Tooltip>
	)
}
